import {
  forwardRef,
  useRef,
  useImperativeHandle,
  useEffect,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import styles from './SearchInput.module.css';

interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  shortcutKeys?: string[];
  /** Called when Ctrl+K / Cmd+K is pressed (search mode) */
  onSearchMode?: () => void;
  /** Called when Ctrl+P / Cmd+P is pressed (command palette mode) */
  onCommandMode?: () => void;
}

const isMac =
  typeof navigator !== 'undefined' &&
  /mac|iphone|ipad/i.test(navigator.userAgent);

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      icon,
      iconPosition = 'left',
      shortcutKeys,
      onSearchMode,
      onCommandMode,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasShortcut = Boolean(shortcutKeys);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      if (!hasShortcut) return undefined;

      const handleKeyDown = (e: KeyboardEvent) => {
        const modKey = isMac ? e.metaKey : e.ctrlKey;
        if (modKey && e.key.toLowerCase() === 'k') {
          e.preventDefault();
          inputRef.current?.focus();
          onSearchMode?.();
        }
        if (modKey && e.key.toLowerCase() === 'p') {
          e.preventDefault();
          inputRef.current?.focus();
          onCommandMode?.();
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [hasShortcut, onSearchMode, onCommandMode]);

    const hasValue = Boolean(inputProps.value || inputProps.defaultValue);

    const iconEl = icon && <span className={styles.icon}>{icon}</span>;

    const shortcutEl = shortcutKeys && (
      <span className={styles.shortcut} data-hidden={hasValue || undefined}>
        {shortcutKeys.map((key) =>
          key === '/' ? (
            <span key={key} className={styles.separator}>
              {key}
            </span>
          ) : (
            <kbd key={key} className={styles.kbd}>
              {key}
            </kbd>
          )
        )}
      </span>
    );

    return (
      <label className={styles.wrapper}>
        {iconPosition === 'left' && iconEl}
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          {...inputProps}
        />
        {shortcutEl}
        {iconPosition === 'right' && iconEl}
      </label>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
